/*
  The project page's AI features — "What is blocking" wording (04 #9) and "DN from photo" (05 #10) — send project
  data to Google, so they are OFF by default: they need the `ai_extras` feature (Admin › Integrations, off until an
  admin switches it on — the AI page's `ai` is not enough) AND the server switch AI_PROJECT_FEATURES_ENABLED
  (GET /project-ai/status; docs/INTEGRATIONS_SETUP.md › AI extras / Project page AI).

    const projectAi = useProjectAi();
    // { enabled, loading, extras (useFeature('ai_extras')), extrasOn, ai (useFeature('ai')), aiOn, switchEnv }
    <ProjectAiSwitchedOff title="…" text="…" />   // the "switched off on this server" note

  `ai` / `aiOn` are the AI page (e.g. BlockersCard's "Ask a follow-up" opens it) and need only the `ai` integration.
  The server is only asked once `ai_extras` reads "on", so pages (and tests) without it never call it.
*/
import { useEffect, useState } from 'react';
import { PowerOff } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { toneClass } from '@/lib/status';
import { AI_EXTRAS } from '@/lib/integrations';
import { useAuth } from '@/contexts/AuthContext';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { StatusPill } from '@/components/ui/status-pill';

export const PROJECT_AI_SWITCH = 'AI_PROJECT_FEATURES_ENABLED';
const OFF = { loading: false, enabled: false, switchedOn: false, switchEnv: '' };

export function useProjectAi() {
  const ai = useFeature('ai');
  const extras = useFeature(AI_EXTRAS);
  const extrasOn = Boolean(extras?.enabled);
  const [state, setState] = useState(OFF);

  useEffect(() => {
    if (!extrasOn) {
      setState(OFF);
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    api.get('/project-ai/status')
      .then((res) => {
        if (cancelled) return;
        setState({
          loading: false,
          enabled: Boolean(res.data?.enabled),
          switchedOn: Boolean(res.data?.switched_on),
          switchEnv: res.data?.switch_env || '',
        });
      })
      .catch(() => { if (!cancelled) setState(OFF); });
    return () => { cancelled = true; };
  }, [extrasOn]);

  return {
    ...state,
    ai,
    aiOn: Boolean(ai?.enabled),
    extras,
    extrasOn,
    loading: Boolean(extras?.loading) || state.loading,
  };
}

const ADMIN_ROLES = ['Admin', 'Super Admin'];

/** "<feature> is switched off on this server" — AI extras is on, the project-page server switch is not. */
export function ProjectAiSwitchedOff({ title, text, className, testId = 'project-ai-switched-off' }) {
  const auth = useAuth();
  const admin = ADMIN_ROLES.includes(String(auth?.user?.role || '').trim());
  return (
    <section
      data-testid={testId}
      data-status="off"
      aria-label={title}
      className={cn('flex items-start gap-3.5 rounded-card border border-line bg-surface p-4 shadow-card sm:p-5', className)}
    >
      <span className={cn('ui-icon-tile h-10 w-10 rounded-xl', toneClass('amber'))} aria-hidden="true">
        <PowerOff className="h-[19px] w-[19px]" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-heading text-[15px] font-bold text-ink">{title}</h3>
          <StatusPill size="sm" tone="amber" label="Switched off" />
        </div>
        <p className="text-[13px] text-ink-2">
          It sends project data to Google Gemini, so it stays off until the company decides to use it.
          {admin
            ? <> IT switches it on in the server settings (<code className="font-mono text-[12px]">{PROJECT_AI_SWITCH}</code>, see Integrations setup).</>
            : ' Ask IT or your administrator.'}
        </p>
        {text ? <p className="text-[13px] text-ink-3">{text}</p> : null}
      </div>
    </section>
  );
}
